'use client';

import { useEffect, useState } from 'react';
import { getClientTimezone, setClientTimezone } from '@/lib/utils/timezone';
import { guessTimezone } from '@/lib/utils/date';

/**
 * The viewer's timezone.
 *
 * Detection only works in a browser, so a server render has nothing to go on —
 * unless the page hands over the `user_timezone` cookie this hook wrote on an
 * earlier visit. Given that, the first paint is already right and nothing has to
 * wait for hydration; without it (a first visit) the hook still returns null
 * until mount, and callers hold their space rather than print a guess.
 *
 * Detection always wins in the end: a traveller's stale cookie is corrected on
 * mount and rewritten.
 */
export function useTimezone(initial: string | null = null): string | null {
  const [timezone, setTimezone] = useState<string | null>(initial);

  useEffect(() => {
    // Get timezone from cookie or detect new one
    const storedTimezone = getClientTimezone();
    setTimezone(storedTimezone);

    // Double-check detection matches stored value
    const detectedTimezone = guessTimezone();
    if (detectedTimezone !== storedTimezone) {
      setClientTimezone(detectedTimezone);
      setTimezone(detectedTimezone);
    }
  }, []);

  return timezone;
}
